/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useMessages.ts - Hook para gestión de mensajes
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Hook personalizado para manejar mensajes de conversaciones
 * @version 1.0.0
 * @architecture WASP Frontend → Proxy → Bot
 * 
 * ENDPOINTS USADOS:
 * - GET  /api/whatsapp/wasp/conversation-detail - Obtener mensajes (paginado)
 * - POST /api/whatsapp/wasp/send                - Enviar mensaje
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { getHatateToken } from '../helpers/utils';

// URLs base del proxy WASP
const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';

// Número de mensajes por página
const PAGE_SIZE = 10;

// Tipos
export interface Message {
  timestamp: string;
  sender: string;
  text: string;
  id?: string;
  type?: 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker';
  mediaUrl?: string;
}

export interface UseMessagesOptions {
  contactId: string;
  pageSize?: number;
  autoUpdate?: boolean;
  updateInterval?: number;
  onError?: (error: string) => void;
}

export interface UseMessagesReturn {
  messages: Message[];
  displayedMessages: Message[];
  loading: boolean;
  loadingMore: boolean;
  sending: boolean;
  error: string | null;
  page: number;
  hasMoreMessages: boolean;
  totalMessages: number;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (text: string, mediaData?: SendMediaData) => Promise<boolean>;
  refreshMessages: () => Promise<void>;
  clearMessages: () => void;
}

export interface SendMediaData {
  type: 'image' | 'video' | 'audio' | 'document' | 'sticker';
  data: string; // Base64 o URL
  filename?: string;
  caption?: string;
}

/**
 * Hook personalizado para gestión de mensajes
 * 
 * @param options - Opciones de configuración
 * @returns Estado y funciones de mensajes
 * 
 * @example
 * ```tsx
 * const { 
 *   messages, 
 *   sendMessage, 
 *   loadMoreMessages 
 * } = useMessages({ contactId: '56912345678' });
 * 
 * // Enviar mensaje
 * await sendMessage('Hola!');
 * 
 * // Cargar más mensajes (paginación)
 * await loadMoreMessages();
 * ```
 */
export const useMessages = (options: UseMessagesOptions): UseMessagesReturn => {
  const {
    contactId,
    pageSize = PAGE_SIZE,
    autoUpdate = false,
    updateInterval = 10000,
    onError
  } = options;

  // Estados
  const [messages, setMessages] = useState<Message[]>([]);
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const [totalMessages, setTotalMessages] = useState<number>(0);

  // Refs
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const lastMessageCountRef = useRef<number>(0);

  /**
   * Obtener token de autenticación
   */
  const getAuthToken = useCallback((): string => {
    return localStorage.getItem('authToken') || '';
  }, []);

  /**
   * Obtener mensajes desde el servidor
   */
  const fetchMessages = useCallback(
  async (currentPage: number, append: boolean = false): Promise<void> => {
    try {
      const token = getHatateToken(); // ✅ Usar getHatateToken en lugar de localStorage
      
      if (!contactId) {
        throw new Error('ContactId es requerido');
      }

      // Calcular límite para paginación
      const limit = currentPage * pageSize;

      const response = await axios.get<{
        success: boolean;
        contactId: string;
        messageCount: number;
        messages: Message[];
      }>(
        `${API_BASE_URL}/conversation-detail`,
        {
          params: {
            contactId,
            limit
          },
          headers: {
            'x-api-key': 'anubis-whatsapp-api-key', // ✅ Agregar API key
            'Authorization': token, // ✅ Ya viene con Bearer
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

        if (!isMountedRef.current) return;

        if (response.data.success && response.data.messages) {
  const fetchedMessages = response.data.messages.map((msg: any) => {
  // Determinar quién envió el mensaje basado en campo 'sender'
  let sender: string;
  
  if (msg.sender === 'bot') {
    sender = 'bot'; // Mensaje enviado por mí
  } else if (msg.sender === 'bot' || msg.sender === 'contact') {
    sender = 'contact'; // Mensaje recibido (del bot o contacto)
  } else {
    // Fallback: Si no se puede determinar, asumir recibido
    sender = 'contact';
  }
  
  return {
    timestamp: msg.timestamp,
    sender: sender,
    text: msg.body || msg.text || '',
    id: msg.messageId || msg.id || `msg-${Date.now()}-${Math.random()}`,
    type: msg.type || 'text',
    mediaUrl: msg.mediaUrl
  };
});
          const messageCount = response.data.messageCount;

          setMessages(fetchedMessages);
          setTotalMessages(messageCount);
          
          // Verificar si hay más mensajes
          setHasMoreMessages(fetchedMessages.length < messageCount);

          // Actualizar mensajes mostrados (últimos N mensajes)
          const messagesToShow = fetchedMessages.slice(-limit);
          setDisplayedMessages(messagesToShow);

          console.log('📊 DEBUG displayedMessages:', messagesToShow);
          console.log('📊 DEBUG cantidad:', messagesToShow.length);

          setError(null);
          lastMessageCountRef.current = messageCount;

          console.log(`✅ Mensajes cargados (página ${currentPage}):`, messagesToShow.length);
        } else {
          throw new Error('Respuesta inválida del servidor');
        }
      } catch (err) {
        if (!isMountedRef.current) return;

        const errorMessage = err instanceof AxiosError
          ? err.response?.data?.message || err.message
          : 'Error al cargar mensajes';

        console.error('❌ Error cargando mensajes:', errorMessage);
        setError(errorMessage);

        if (onError) {
          onError(errorMessage);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [contactId, pageSize, getAuthToken, onError]
  );

  /**
   * Cargar más mensajes (paginación)
   */
  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (loadingMore || !hasMoreMessages) return;

    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchMessages(nextPage, true);
  }, [page, hasMoreMessages, loadingMore, fetchMessages]);

  /**
   * Refrescar mensajes
   */
  const refreshMessages = useCallback(async (): Promise<void> => {
    setLoading(true);
    await fetchMessages(page, false);
  }, [page, fetchMessages]);

  /**
   * Enviar mensaje
   * 
   * @param text - Texto del mensaje
   * @param mediaData - Datos multimedia opcionales
   * @returns true si se envió correctamente
   */
  const sendMessage = useCallback(
    async (text: string, mediaData?: SendMediaData): Promise<boolean> => {
      if (!text.trim() && !mediaData) {
        console.warn('⚠️ Mensaje vacío');
        return false;
      }

      setSending(true);

      try {
        const token = getHatateToken();
        
        if (!token) {
          throw new Error('No hay token de autenticación');
        }

        // Preparar payload
        const payload: any = {
          to: contactId,
          message: text.trim()
        };

        // Agregar datos multimedia si existen
        if (mediaData) {
          payload.mediaType = mediaData.type;
          payload.mediaData = mediaData.data;
          
          if (mediaData.filename) {
            payload.filename = mediaData.filename;
          }
          
          if (mediaData.caption) {
            payload.caption = mediaData.caption;
          }
        }

        const response = await axios.post<{
          success: boolean;
          message: string;
        }>(
          `${API_BASE_URL}/send`,
          payload,
          {
            headers: {
              'x-api-key': 'anubis-whatsapp-api-key',
              'Authorization': token,
              'Content-Type': 'application/json'
            },
            timeout: 30000
          }
        );

        if (response.data.success) {
          console.log('✅ Mensaje enviado correctamente');

          const optimisticMessage: Message = {
    timestamp: new Date().toISOString(),
    sender: 'bot',  // Tu mensaje
    text: text.trim(),
    id: `temp-${Date.now()}`,
    type: 'text'
  };
  
  // Agregar a displayedMessages inmediatamente
  setDisplayedMessages(prev => [...prev, optimisticMessage]);
  
          
          // Refrescar mensajes después de enviar
          setTimeout(() => {
            refreshMessages();
          }, 2000);

          return true;
        }

        return false;
      } catch (err) {
        const errorMessage = err instanceof AxiosError
          ? err.response?.data?.message || err.message
          : 'Error al enviar mensaje';

        console.error('❌ Error enviando mensaje:', errorMessage);

        if (onError) {
          onError(errorMessage);
        }

        return false;
      } finally {
        setSending(false);
      }
    },
    [contactId, getAuthToken, refreshMessages, onError]
  );

  /**
   * Limpiar mensajes
   */
  const clearMessages = useCallback((): void => {
    setMessages([]);
    setDisplayedMessages([]);
    setPage(1);
    setHasMoreMessages(true);
    setError(null);
  }, []);

  /**
   * Cargar mensajes iniciales
   */
  useEffect(() => {
    isMountedRef.current = true;
    
    if (contactId) {
      setLoading(true);
      fetchMessages(1, false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [contactId, fetchMessages]);

  /**
   * Auto-actualización de mensajes
   */
  useEffect(() => {
    if (autoUpdate && updateInterval > 0 && contactId) {
      intervalRef.current = setInterval(() => {
        fetchMessages(page, false);
      }, updateInterval);

      console.log(`🔄 Auto-update mensajes activado: cada ${updateInterval}ms`);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [autoUpdate, updateInterval, contactId, page, fetchMessages]);

  return {
    messages,
    displayedMessages,
    loading,
    loadingMore,
    sending,
    error,
    page,
    hasMoreMessages,
    totalMessages,
    loadMoreMessages,
    sendMessage,
    refreshMessages,
    clearMessages
  };
};

export default useMessages;
